// src/components/document/PagedEditor.tsx
// Word-style paged document editor for controlled/formal documents.
// Renders blocks inside A4/Letter page boundaries with margins, headers, footers, page numbers.
//
// Approach: one continuous TipTap editor inside a page-width container.
// A useEffect measures block positions and injects non-editable "page break"
// spacer elements at each page boundary. The spacers push content down so
// it never hides behind headers/footers. Headers/footers/page-backgrounds
// are absolutely positioned overlays keyed to the known page positions.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Editor } from '@tiptap/react';
import { useI18n } from '@react/hooks';
import { Select } from '@/components/Select';
import { DocumentEditor } from './editor/DocumentEditor';
import { EditorToolbar } from './editor/EditorToolbar';
import { Ruler } from './Ruler';
import { PageBreak } from './editor/page-break-extension';
import {
  getPageDimensionsPx,
  getContentHeightPx,
  DEFAULT_PAGE_SETTINGS,
  formatDocumentNumber,
  getDocumentVersion,
  formatDate,
  buildHeaderFooterFromSettings,
} from '@/features/document/logic';
import type { DocumentBlock, PageSettings } from '@core/types/document';

// Spacer class used by the page-break plugin for styling
const SPACER_CLASS = 'paged-editor-break-spacer';

// ---------------------------------------------------------------------------
// Page settings bar (paper size, orientation, margins toggle)
// ---------------------------------------------------------------------------

function PageSettingsBar({
  settings,
  onChange,
  t,
  rulerVisible,
  onToggleRuler,
}: {
  settings: any;
  onChange: (patch: Partial<PageSettings>) => void;
  t: (k: string) => string;
  rulerVisible: boolean;
  onToggleRuler: () => void;
}) {
  const paperSize = settings?.paper_size || 'PAPER_SIZE_A4';
  const orientation = settings?.orientation || 'PAGE_ORIENTATION_PORTRAIT';

  return (
    <div className="flex items-center gap-3 mb-4 px-1 text-xs text-muted-foreground">
      <Select
        value={paperSize}
        onChange={(e) => onChange({ paper_size: e.target.value as any })} fullWidth={false} aria-label={t('document.paged.paperSize')}>
        <option value="PAPER_SIZE_A4">A4</option>
        <option value="PAPER_SIZE_A3">A3</option>
        <option value="PAPER_SIZE_LETTER">Letter</option>
        <option value="PAPER_SIZE_LEGAL">Legal</option>
      </Select>

      <div className="inline-flex rounded border border-border overflow-hidden">
        <button
          onClick={() => onChange({ orientation: 'PAGE_ORIENTATION_PORTRAIT' as any })}
          className={`px-2 py-1 ${orientation === 'PAGE_ORIENTATION_PORTRAIT' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
          aria-label={t('document.paged.portrait')}
          title={t('document.paged.portrait')}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="5" y="2" width="14" height="20" rx="1" />
          </svg>
        </button>
        <button
          onClick={() => onChange({ orientation: 'PAGE_ORIENTATION_LANDSCAPE' as any })}
          className={`px-2 py-1 ${orientation === 'PAGE_ORIENTATION_LANDSCAPE' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
          aria-label={t('document.paged.landscape')}
          title={t('document.paged.landscape')}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="2" y="5" width="20" height="14" rx="1" />
          </svg>
        </button>
      </div>

      <button
        onClick={onToggleRuler}
        className={`px-2 py-1 rounded border border-border ${rulerVisible ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'}`}
        aria-label={t('document.paged.ruler')}
        title={t('document.paged.ruler')}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <rect x="1" y="8" width="22" height="8" rx="1" />
          <line x1="5" y1="8" x2="5" y2="12" />
          <line x1="9" y1="8" x2="9" y2="14" />
          <line x1="13" y1="8" x2="13" y2="12" />
          <line x1="17" y1="8" x2="17" y2="14" />
          <line x1="21" y1="8" x2="21" y2="12" />
        </svg>
      </button>

      <span className="text-muted-foreground/60">{t('document.paged.zoomHint')}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main PagedEditor
// ---------------------------------------------------------------------------

interface PagedEditorProps {
  documentId: string;
  document: any;
  initialBlocks?: DocumentBlock[];
  onBlockUpdate?: (block: DocumentBlock) => void;
  onBlockCreate?: (block: DocumentBlock, afterBlockId?: string) => void;
  onBlockDelete?: (blockId: string) => void;
  onPageSettingsChange?: (settings: Partial<PageSettings>) => void;
  onEditorReady?: (editor: Editor) => void;
  readOnly?: boolean;
}

export function PagedEditor({
  documentId,
  document: doc,
  initialBlocks,
  onBlockUpdate,
  onBlockCreate,
  onBlockDelete,
  onPageSettingsChange,
  onEditorReady,
  readOnly = false,
}: PagedEditorProps) {
  const { t, locale } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const editorWrapRef = useRef<HTMLDivElement>(null);
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const handleEditorReady = useCallback((editor: Editor) => {
    setEditorInstance(editor);
    onEditorReady?.(editor);
  }, [onEditorReady]);

  const settings = doc?.page_settings || DEFAULT_PAGE_SETTINGS;
  const { widthPx, heightPx, marginsPx } = useMemo(
    () => getPageDimensionsPx(doc),
    [settings?.paper_size, settings?.orientation, settings?.margins],
  );
  const contentHeight = useMemo(() => getContentHeightPx(doc), [doc]);

  // Height of the break zone: bottom margin + visual gap + top margin of next page
  // + headerFooterSpace (40px) to account for header/footer within margins
  const breakZoneHeight = marginsPx.bottom + 24 + marginsPx.top + 40;

  // -----------------------------------------------------------------------
  // Page-break via TipTap PageBreak extension (PM decorations)
  // -----------------------------------------------------------------------
  const [pageCount, setPageCount] = useState(1);

  const pageBreakExtension = useMemo(
    () =>
      PageBreak.configure({
        contentHeight,
        breakZoneHeight,
        onPageCount: setPageCount,
      }),
    [contentHeight, breakZoneHeight],
  );

  // -----------------------------------------------------------------------
  // Header/footer text
  // -----------------------------------------------------------------------
  const docNumber = formatDocumentNumber(doc);
  const version = getDocumentVersion(doc);
  const hasSettingsHeaderFooter = !!(settings?.header || settings?.footer);

  const headerText = useMemo(() => {
    if (hasSettingsHeaderFooter) {
      return buildHeaderFooterFromSettings(settings, {
        document_number: docNumber || '',
        title: doc?.title || '',
        version: version ? `v${version}` : '',
        date: doc?.updated_at ? formatDate(doc.updated_at, locale) : '',
        department: doc?.department || '',
      }).header;
    }
    return {
      left: docNumber || '',
      center: doc?.title || '',
      right: version ? `v${version}` : '',
    };
  }, [docNumber, doc?.title, version, hasSettingsHeaderFooter, settings, doc?.updated_at, doc?.department, locale]);

  const footerTemplate = useMemo(() => {
    if (hasSettingsHeaderFooter) {
      return settings?.footer || { left: '', center: '{page}', right: '' };
    }
    return null;
  }, [hasSettingsHeaderFooter, settings]);

  const handleSettingsChange = useCallback((patch: Partial<PageSettings>) => {
    onPageSettingsChange?.(patch);
  }, [onPageSettingsChange]);

  // Ruler visibility
  const [rulerVisible, setRulerVisible] = useState(false);
  const toggleRuler = useCallback(() => setRulerVisible((v) => !v), []);

  const handleRulerMarginsChange = useCallback(
    (margins: { left_mm?: number; right_mm?: number }) => {
      const current = settings?.margins || { top_mm: 20, bottom_mm: 20, left_mm: 30, right_mm: 15 };
      onPageSettingsChange?.({
        margins: {
          ...current,
          ...(margins.left_mm != null ? { left_mm: margins.left_mm } : {}),
          ...(margins.right_mm != null ? { right_mm: margins.right_mm } : {}),
        },
      });
    },
    [settings, onPageSettingsChange],
  );

  // Scale to fit viewport
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const containerWidth = entries[0].contentRect.width;
      const available = containerWidth - 48;
      if (available < widthPx) {
        setScale(Math.max(0.5, available / widthPx));
      } else {
        setScale(1);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [widthPx]);

  // -----------------------------------------------------------------------
  // Helper: build footer for a given page number
  // -----------------------------------------------------------------------
  const buildFooter = useCallback((pageNum: number) => {
    const startNum = (settings?.page_numbering_start || 1) - 1;
    if (footerTemplate) {
      return buildHeaderFooterFromSettings(
        { footer: footerTemplate },
        {
          document_number: docNumber || '',
          title: doc?.title || '',
          version: version ? `v${version}` : '',
          date: doc?.updated_at ? formatDate(doc.updated_at, locale) : '',
          department: doc?.department || '',
          page: pageNum + startNum,
          total_pages: pageCount,
        },
      ).footer;
    }
    return {
      left: doc?.department || '',
      center: `${pageNum + startNum}`,
      right: doc?.updated_at ? formatDate(doc.updated_at, locale) : '',
    };
  }, [footerTemplate, docNumber, doc, version, locale, pageCount, settings]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  // Total visual height of the document:
  // pageCount pages of heightPx each + (pageCount-1) gaps of 24px
  const totalDocHeight = pageCount * heightPx + Math.max(0, pageCount - 1) * 24;

  return (
    <div ref={containerRef} className="w-full">
      {!readOnly && (
        <PageSettingsBar
          settings={settings}
          onChange={handleSettingsChange}
          t={t}
          rulerVisible={rulerVisible}
          onToggleRuler={toggleRuler}
        />
      )}

      {!readOnly && <EditorToolbar editor={editorInstance} />}

      <div
        className="overflow-x-auto py-6 bg-muted/30 rounded-lg"
        style={{
          transformOrigin: 'top center',
          transform: scale < 1 ? `scale(${scale})` : undefined,
          width: scale < 1 ? `${100 / scale}%` : undefined,
        }}
      >
        {/* Ruler — aligned with page width, above pages */}
        <Ruler
          widthPx={widthPx}
          marginLeftPx={marginsPx.left}
          marginRightPx={marginsPx.right}
          visible={rulerVisible && !readOnly}
          onMarginsChange={handleRulerMarginsChange}
        />

        <div className="relative mx-auto" style={{ width: widthPx }}>

          {/* Page backgrounds (white sheets with shadows) */}
          {Array.from({ length: pageCount }).map((_, i) => (
            <div
              key={`bg-${i}`}
              className="absolute bg-card border border-border shadow-md"
              style={{
                top: i * (heightPx + 24),
                left: 0,
                width: widthPx,
                height: heightPx,
              }}
            />
          ))}

          {/* Page headers */}
          {Array.from({ length: pageCount }).map((_, i) => {
            const showHeader = !(i === 0 && settings?.first_page_different) && headerText;
            if (!showHeader) return null;
            return (
              <div
                key={`hdr-${i}`}
                className="absolute left-0 right-0 flex items-end justify-between text-[10px] text-muted-foreground border-b border-border/50 z-10"
                style={{
                  top: i * (heightPx + 24),
                  paddingLeft: marginsPx.left,
                  paddingRight: marginsPx.right,
                  paddingTop: Math.round(marginsPx.top * 0.4),
                  paddingBottom: 6,
                  height: marginsPx.top,
                }}
              >
                <span>{headerText.left || ''}</span>
                <span>{headerText.center || ''}</span>
                <span>{headerText.right || ''}</span>
              </div>
            );
          })}

          {/* Page footers */}
          {Array.from({ length: pageCount }).map((_, i) => {
            const footer = buildFooter(i + 1);
            return (
              <div
                key={`ftr-${i}`}
                className="absolute left-0 right-0 flex items-start justify-between text-[10px] text-muted-foreground border-t border-border/50 z-10"
                style={{
                  top: i * (heightPx + 24) + heightPx - marginsPx.bottom,
                  paddingLeft: marginsPx.left,
                  paddingRight: marginsPx.right,
                  paddingTop: 6,
                  height: marginsPx.bottom,
                }}
              >
                <span>{footer?.left || ''}</span>
                <span className="tabular-nums">{footer?.center || ''}</span>
                <span>{footer?.right || ''}</span>
              </div>
            );
          })}

          {/* Editor content — flows through all pages.
              Padding matches page margins. Spacer divs injected by
              injectSpacers() push content past footer/gap/header zones. */}
          <div
            ref={editorWrapRef}
            className="relative z-[5]"
            style={{
              paddingLeft: marginsPx.left,
              paddingRight: marginsPx.right,
              paddingTop: marginsPx.top,
              paddingBottom: marginsPx.bottom,
              minHeight: totalDocHeight,
            }}
          >
            <DocumentEditor
              documentId={documentId}
              initialBlocks={initialBlocks}
              onBlockUpdate={onBlockUpdate}
              onBlockCreate={onBlockCreate}
              onBlockDelete={onBlockDelete}
              readOnly={readOnly}
              extraExtensions={[pageBreakExtension]}
              onEditorReady={handleEditorReady}
            />
          </div>

        </div>
      </div>

      {/* Spacer styling: make break spacers visually show the gap */}
      <style>{`
        .${SPACER_CLASS} {
          position: relative;
          background: transparent;
        }
        .${SPACER_CLASS}::before {
          content: '';
          position: absolute;
          left: -${marginsPx.left}px;
          right: -${marginsPx.right}px;
          top: 0;
          height: 100%;
          background: var(--muted, #f1f5f9);
          opacity: 0.3;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
